import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Data } from "@angular/router";
import { signal } from "@angular/core";
import { UtilityService } from "@core/utility.service";
import { Subject, Subscription } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import {
  resolveInlineCandidateList,
  selectDeviceInlineNotification,
  shouldShowAddHomeScreenBanner,
} from "src/app/components/add-home-screen-banner/add-home-screen.helpers";
import { VoucherService } from "src/app/services/vouchers.service";
import { AuthService } from "@core/auth.service"; 
import { NotificationsService } from "src/app/services/notifications.service";

@Injectable({ providedIn: 'root' })
export class VoucherNotificationService {
  public inlineNotificationDetails: Data;
  public countdownSeconds: number | null;
  public claimTimerDisplay = "";
  public isVoucherClaimedSignal = signal<boolean>(false);
  private destroy$ = new Subject<void>();
  private expiryTimer: ReturnType<typeof setTimeout> | undefined;
  private lastPageDetails: Data | undefined;
  private countdownSub: Subscription | undefined;

  constructor(
    private voucherService: VoucherService,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private router: Router
  ) {}

  getVoucherDetails(): void {
    this.voucherService
      ?.getVoucherPageData()
      ?.pipe(
        takeUntil(this.destroy$),
        map((voucherData: Data) => {
          this.validateNotifications(voucherData);
        }),
      )
      ?.subscribe();
  }

  validateNotifications(syndicateDetails: Data): void {
    try {
      if (this.expiryTimer) {
        clearTimeout(this.expiryTimer);
        this.expiryTimer = undefined;
      }
      this.lastPageDetails = syndicateDetails;

      const candidateList = resolveInlineCandidateList(syndicateDetails);
      const notification = this.selectActiveInlineNotification(candidateList);
      if (!notification) {
        this.dismissExpiredToRebuild(candidateList);
        this.clearCountdownSubscription();
        this.inlineNotificationDetails = {};
        this.countdownSeconds = null;
        return;
      }

      this.applyNotificationDetails(notification);
      this.scheduleExpiryRevalidation(notification);
    } catch (error) {
      return error;
    }
  }

  private selectActiveInlineNotification(inlineNotifications: Data[] | undefined): Data | null {
    return selectDeviceInlineNotification(inlineNotifications as Array<Record<string, unknown>>);
  }

  private dismissExpiredToRebuild(inlineNotifications: Data[] | undefined): void {
    if (!Array.isArray(inlineNotifications)) {
      return;
    }

    const now = Date.now();
    for (const notification of inlineNotifications) {
      if (notification?.dismissed) {
        continue;
      }
      const expiryMs = notification?.expiry_date?.seconds
        ? notification.expiry_date.seconds * 1000
        : 0;
      if (!expiryMs || expiryMs <= now) {
        const path = notification?.notification_id?.path;
        if (path) {
          notification.dismissed = true;
          this.notificationsService.updateClaiminlineVoucherDismiss(path);
        }
        return;
      }
    }
  }

  private applyNotificationDetails(notification: Data): void {
    this.inlineNotificationDetails = { ...(notification || {}) };
    const inlineId = this.inlineNotificationDetails?.notification_id?.id;
    const expirySeconds = this.inlineNotificationDetails?.expiry_date?.seconds;
    this.clearCountdownSubscription();
    if (expirySeconds == null || !inlineId) {
      return;
    }

    this.countdownSub = this.notificationsService
      ?.startCountdownMonitor(expirySeconds, inlineId)
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.countdownSeconds = res;
      });
  }

  private scheduleExpiryRevalidation(notification: Data): void {
    const expiryMs = notification?.expiry_date?.seconds
      ? notification.expiry_date.seconds * 1000
      : 0;
    if (!expiryMs) {
      return;
    }

    const delay = expiryMs - Date.now();
    const run = () => {
      this.expiryTimer = undefined;
      const next = this.selectActiveInlineNotification(resolveInlineCandidateList(this.lastPageDetails));
      if (next) {
        this.applyNotificationDetails(next);
        this.scheduleExpiryRevalidation(next);
        return;
      }
      const path = notification?.notification_id?.path;
      if (path) {
        notification.dismissed = true;
        this.notificationsService.updateClaiminlineVoucherDismiss(path);
      }
      this.clearCountdownSubscription();
      this.inlineNotificationDetails = {};
      this.countdownSeconds = null;
    };

    if (delay <= 0) {
      run();
      return;
    }

    this.expiryTimer = setTimeout(run, delay + 100);
  }

  private clearCountdownSubscription(): void {
    this.countdownSub?.unsubscribe();
    this.countdownSub = undefined;
  }

  handleInlineNotification(notificationDetails: Data, languageCode: string): void {
    if (notificationDetails?.action?.type === "nav" && notificationDetails?.action?.details) {
      const actionDetails = notificationDetails.action.details;

      if (actionDetails?.includes("http")) {
        this.voucherService?.openTypeFormForFeedback(notificationDetails, languageCode);
      } else {
        this.router.navigateByUrl(actionDetails)?.catch((error) => error);
      }
      // To dismiss current notification
      this.feedBackDismiss();
    }
    if (notificationDetails?.action?.type === "script") {
      this.isVoucherClaimedSignal.set(true);
      this.notificationsService.onClaiminlinevoucher(notificationDetails).then((result: Data) => {
        if (result) this.isVoucherClaimedSignal.set(false);
      }).catch((error) => {
        this.authService.errorHandlerLogs("Error claiming voucher:", error);
      });
    }
  }

  isPushNotification(): boolean {
    const actionDetails = this.inlineNotificationDetails?.action?.details?.toLowerCase();
    return actionDetails === "enable_push_notifications" || actionDetails === "enable push notifications";
  }

  isAddHomeScreen(): boolean {
    const actionDetails = this.inlineNotificationDetails?.action?.details?.toLowerCase();
    return shouldShowAddHomeScreenBanner(
      actionDetails,
      CommonVariable?.device?.operatingSystem,
      UtilityService.isAppInstalledOnHomePage()
    );
  }

  getNotificationDetails(syndicateDetails: Data): void {
    const notification = this.selectActiveInlineNotification(resolveInlineCandidateList(syndicateDetails));
    if (!notification) {
      this.clearCountdownSubscription();
      this.inlineNotificationDetails = {};
      this.countdownSeconds = null;
      return;
    }
    this.applyNotificationDetails(notification);
  }

  // This method used to dismiss dialog
  feedBackDismiss(): void {
    // Path based on the provided type
    const route = this.inlineNotificationDetails?.notification_id?.path;
    if (!route) {
      return;
    }
    const doc_id = route?.substring(route.lastIndexOf("/") + 1) || null;
    const path_id = "pGxBYtRO4mdgNkboYdSy";
    const doc_data = { dismissed: true, viewed: true };
    const dv = { doc_data: doc_id };
    const method = "update";

    this.authService
      .getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        dv,
        method,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          this.authService.errorHandlerLogs("Error updating feedback:", error);
        },
      });
  }

  handleCountdownFinish(): void {
    this.notificationsService.updateClaiminlineVoucherDismiss(this.inlineNotificationDetails?.notification_id?.path);
  }

  destroy(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = undefined;
    }
    this.clearCountdownSubscription();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
